/**
 * TtlService — client-side TTL keypair generation and persistence.
 *
 * Security rules:
 * - The X25519 private key is encrypted with the user's master key before
 *   leaving this module. The plaintext private key is zeroized immediately
 *   after encryption and never serialised or logged.
 * - Only the public key and the encrypted private key (+ nonce + alg) are
 *   sent to the server.
 */

import sodium from 'libsodium-wrappers-sumo'
import ApiService from './ApiService'

export type TtlKeypairPayload = {
    /** Base64-encoded X25519 public key — stored in plain server-side. */
    public_key: string
    /** Base64-encoded X25519 private key encrypted with the user's master key. */
    encrypted_private_key: string
    /** Base64-encoded nonce used when encrypting the private key. */
    nonce: string
    /** Identifies the key type and encryption scheme. */
    alg: 'x25519-secretbox'
}

/**
 * Generate an X25519 keypair and encrypt the private key with the user's
 * master key using `crypto_secretbox_easy`.
 *
 * @param masterKey - 32-byte master key derived from the user's master password.
 *                    Must remain in memory only — never serialised.
 * @returns         - `TtlKeypairPayload` ready to be sent to the server.
 *
 * ⚠️  The plaintext private key is zeroized inside this function before returning.
 */
export function generateTtl(masterKey: Uint8Array): TtlKeypairPayload {
    const keypair = sodium.crypto_box_keypair()

    const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES)

    const encryptedPrivateKey = sodium.crypto_secretbox_easy(
        keypair.privateKey,
        nonce,
        masterKey,
    )

    // Zeroize the plaintext private key immediately — it must never leave
    // this function in cleartext.
    sodium.memzero(keypair.privateKey)

    return {
        public_key:            sodium.to_base64(keypair.publicKey),
        encrypted_private_key: sodium.to_base64(encryptedPrivateKey),
        nonce:                 sodium.to_base64(nonce),
        alg:                   'x25519-secretbox',
    }
}

/**
 * Persist the TTL keypair on the server.
 *
 * @param payload - Object returned by `generateTtl()`.
 * @returns       - Server confirmation message.
 */
export async function apiStoreTtl(payload: TtlKeypairPayload) {
    return ApiService.fetchDataWithAxios<{ message: string }>({
        url: '/app/v1/master-password/ttl',
        method: 'post',
        data: payload,
    })
}

const TtlService = {
    generateTtl,
    apiStoreTtl,
}

export default TtlService

